// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import catalogStyles from '../../bank-upload/styles/institution-catalog.module.css';
import formStyles from '../../bank-upload/styles/bank-upload-form.module.css';
import { LinkedLiquiditySection } from '@client/banks/components/shared/linked-liquidity-section';
import { DateInput } from '@shared/ui-kit/components/date-input';
import { Input, Label } from '@shared/ui-kit/components/input';
import { OptionsSelector } from '@shared/ui-kit/components/options-selector';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { DECIMAL_SEPARATORS } from '@shared/ui-kit/utils/constants';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { Typography } from '@mui/material';
import { Controller } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { useTransactionForm } from './use-transaction-form';

export const TransactionForm = defineComponent({
  displayName: 'TransactionForm',
  useSetup: useTransactionForm,
  View: ({
    formId,
    onSubmit,
    currencyOptions,
    accountIbanOptions,
    defaultCurrency,
    linkedLiquidityApiRef,
    onLinkedLiquidityGateChange,
    control,
    handleSubmit,
    t,
    currencyPrefix,
    todayMaxDate,
    transactionTypeOptions,
    subtypeOptions,
    assetTypeOptions,
  }) => (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <fieldset className="m-0 flex min-w-0 flex-col gap-7 border-0 p-0">
        <LinkedLiquiditySection
          currencyOptions={currencyOptions}
          accountIbanOptions={accountIbanOptions}
          defaultCurrency={defaultCurrency}
          apiRef={linkedLiquidityApiRef}
          onGateChange={onLinkedLiquidityGateChange}
          labelI18nKey="aggregator.create-bank.transactions.account"
        />

        <section className={formStyles['form-section']}>
          <Typography
            component="h3"
            variant="subtitle1"
            className={formStyles['section-title']}
          >
            <Translation
              i18nKey="aggregator.create-bank.transactions.section.movement"
              ns="aggregator"
            />
          </Typography>

          <div className={formStyles['field-grid']}>
            <Controller
              control={control}
              name="transactionDate"
              render={({ field, fieldState }) => (
                <div
                  className={classNames(
                    catalogStyles['field-stack'],
                    'md:col-span-2',
                  )}
                >
                  <Label
                    htmlFor="bank-upload-transaction-date"
                    required
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.transactions.transaction-date-time"
                      ns="aggregator"
                    />
                  </Label>
                  <DateInput
                    id="bank-upload-transaction-date"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    granularity="datetime"
                    maxDate={todayMaxDate}
                    error={!!fieldState.error}
                    data-testid="bank-upload-transaction-date"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="amount"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label
                    htmlFor="bank-upload-transaction-amount"
                    required
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.transactions.amount"
                      ns="aggregator"
                    />
                  </Label>
                  <NumericFormat
                    customInput={Input}
                    id="bank-upload-transaction-amount"
                    value={field.value || ''}
                    onValueChange={({ floatValue }) =>
                      field.onChange(floatValue ?? 0)
                    }
                    onBlur={field.onBlur}
                    name={field.name}
                    inputMode="decimal"
                    allowNegative
                    decimalScale={2}
                    thousandSeparator="."
                    decimalSeparator=","
                    allowedDecimalSeparators={DECIMAL_SEPARATORS}
                    prefix={currencyPrefix ? `${currencyPrefix} ` : undefined}
                    error={!!fieldState.error}
                    placeholder={t(
                      'aggregator.create-bank.transactions.amount-placeholder',
                      { ns: 'aggregator' },
                    )}
                    data-testid="bank-upload-transaction-amount"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="currency"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label required error={!!fieldState.error}>
                    <Translation
                      i18nKey="aggregator.create-bank.accounts.currency"
                      ns="aggregator"
                    />
                  </Label>
                  <OptionsSelector
                    optionsAvailable={[...currencyOptions]}
                    optionSelected={field.value ?? ''}
                    onChangeValueSelected={field.onChange}
                    defaultText={t('aggregator.create-bank.accounts.currency', {
                      ns: 'aggregator',
                    })}
                    selectType="white"
                    data-testid="bank-upload-transaction-currency"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="description"
              render={({ field, fieldState }) => (
                <div
                  className={classNames(
                    catalogStyles['field-stack'],
                    'md:col-span-2',
                  )}
                >
                  <Label
                    htmlFor="bank-upload-transaction-description"
                    required
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.transactions.description"
                      ns="aggregator"
                    />
                  </Label>
                  <textarea
                    id="bank-upload-transaction-description"
                    value={field.value ?? ''}
                    onChange={event => field.onChange(event.target.value)}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                    rows={3}
                    className={classNames(
                      'input-common',
                      formStyles['textarea-field'],
                      fieldState.error && 'input-error',
                    )}
                    placeholder={t(
                      'aggregator.create-bank.transactions.description-placeholder',
                      { ns: 'aggregator' },
                    )}
                    data-testid="bank-upload-transaction-description"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label
                    htmlFor="bank-upload-transaction-name"
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.transactions.name"
                      ns="aggregator"
                    />
                  </Label>
                  <Input
                    id="bank-upload-transaction-name"
                    {...field}
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    placeholder={t(
                      'aggregator.create-bank.transactions.name-placeholder',
                      { ns: 'aggregator' },
                    )}
                    data-testid="bank-upload-transaction-name"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="type"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label error={!!fieldState.error}>
                    <Translation
                      i18nKey="aggregator.create-bank.transactions.type"
                      ns="aggregator"
                    />
                  </Label>
                  <OptionsSelector
                    optionsAvailable={[...transactionTypeOptions]}
                    optionSelected={field.value ?? ''}
                    onChangeValueSelected={value =>
                      field.onChange(value || undefined)
                    }
                    defaultText={t(
                      'aggregator.create-bank.transactions.type-placeholder',
                      { ns: 'aggregator' },
                    )}
                    selectType="white"
                    data-testid="bank-upload-transaction-type"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="subtype"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label error={!!fieldState.error}>
                    <Translation
                      i18nKey="aggregator.create-bank.transactions.subtype"
                      ns="aggregator"
                    />
                  </Label>
                  <OptionsSelector
                    optionsAvailable={[...subtypeOptions]}
                    optionSelected={field.value ?? ''}
                    onChangeValueSelected={value =>
                      field.onChange(value || undefined)
                    }
                    defaultText={t(
                      'aggregator.create-bank.transactions.subtype-placeholder',
                      { ns: 'aggregator' },
                    )}
                    selectType="white"
                    data-testid="bank-upload-transaction-subtype"
                  />
                </div>
              )}
            />
          </div>
        </section>

        <section className={formStyles['form-section']}>
          <Typography
            component="h3"
            variant="subtitle1"
            className={formStyles['section-title']}
          >
            <Translation
              i18nKey="aggregator.create-bank.transactions.section.additional-data"
              ns="aggregator"
            />
          </Typography>

          <div className={formStyles['field-grid']}>
            <Controller
              control={control}
              name="isin"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label
                    htmlFor="bank-upload-transaction-isin"
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.transactions.isin"
                      ns="aggregator"
                    />
                  </Label>
                  <Input
                    id="bank-upload-transaction-isin"
                    {...field}
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    placeholder={t(
                      'aggregator.create-bank.transactions.isin-placeholder',
                      { ns: 'aggregator' },
                    )}
                    data-testid="bank-upload-transaction-isin"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="quantity"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label
                    htmlFor="bank-upload-transaction-quantity"
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.transactions.quantity"
                      ns="aggregator"
                    />
                  </Label>
                  <NumericFormat
                    customInput={Input}
                    id="bank-upload-transaction-quantity"
                    value={field.value ?? ''}
                    onValueChange={({ floatValue }) =>
                      field.onChange(floatValue ?? undefined)
                    }
                    onBlur={field.onBlur}
                    name={field.name}
                    inputMode="decimal"
                    allowNegative={false}
                    decimalScale={4}
                    thousandSeparator="."
                    decimalSeparator=","
                    allowedDecimalSeparators={DECIMAL_SEPARATORS}
                    error={!!fieldState.error}
                    placeholder={t(
                      'aggregator.create-bank.transactions.quantity-placeholder',
                      { ns: 'aggregator' },
                    )}
                    data-testid="bank-upload-transaction-quantity"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="price"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label
                    htmlFor="bank-upload-transaction-price"
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.transactions.price"
                      ns="aggregator"
                    />
                  </Label>
                  <NumericFormat
                    customInput={Input}
                    id="bank-upload-transaction-price"
                    value={field.value ?? ''}
                    onValueChange={({ floatValue }) =>
                      field.onChange(floatValue ?? undefined)
                    }
                    onBlur={field.onBlur}
                    name={field.name}
                    inputMode="decimal"
                    allowNegative={false}
                    decimalScale={2}
                    thousandSeparator="."
                    decimalSeparator=","
                    allowedDecimalSeparators={DECIMAL_SEPARATORS}
                    prefix={currencyPrefix ? `${currencyPrefix} ` : undefined}
                    error={!!fieldState.error}
                    placeholder={t(
                      'aggregator.create-bank.transactions.price-placeholder',
                      { ns: 'aggregator' },
                    )}
                    data-testid="bank-upload-transaction-price"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="assetType"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label error={!!fieldState.error}>
                    <Translation
                      i18nKey="aggregator.create-bank.transactions.asset-type"
                      ns="aggregator"
                    />
                  </Label>
                  <OptionsSelector
                    optionsAvailable={[...assetTypeOptions]}
                    optionSelected={field.value ?? ''}
                    onChangeValueSelected={value =>
                      field.onChange(value || undefined)
                    }
                    defaultText={t(
                      'aggregator.create-bank.transactions.asset-type-placeholder',
                      { ns: 'aggregator' },
                    )}
                    selectType="white"
                    data-testid="bank-upload-transaction-asset-type"
                  />
                </div>
              )}
            />
          </div>
        </section>
      </fieldset>
    </form>
  ),
});
